import React, { useEffect, useRef, useState } from "react";
import styles from "./AuthLayout.module.css";

// Sign-in hero copy. One state machine conducts both motions: the bold
// audience segment types/deletes on its own rhythm, and the program block
// advances every SECOND audience cycle, swapping during the quiet gap after
// a delete so the two animations never compete for attention.
const AUDIENCES = [
  "Health Systems",
  "Large Practices",
  "Specialty Groups",
  "Your Practice",
  "Cardiologists",
  "Primary Care Groups",
  "Nephrologists",
  "Patients",
  "Endocrinologists",
  "Pulmonologists",
  "Private Practice",
  "Surgeons",
  "Internists",
];

const PROGRAMS = [
  {
    acronym: "RPM",
    name: "Remote Patient Monitoring",
    description:
      "Continuously track patient vitals like blood pressure and glucose from home, giving you real-time visibility between visits.",
  },
  {
    acronym: "CCM",
    name: "Chronic Care Management",
    description:
      "Coordinate ongoing care for patients with two or more chronic conditions through structured monthly check-ins and care planning.",
  },
  {
    acronym: "PCM",
    name: "Principal Care Management",
    description:
      "Deliver focused, dedicated management for a single high-risk condition without the overhead of multi-condition coordination.",
  },
];

const TYPE_MS = 70; // per character while typing
const DELETE_MS = 35; // per character while deleting
const HOLD_MS = 2600; // full word on screen
const GAP_MS = 400; // pause between words
const SWAP_LEAVE_MS = 200; // program block fade-out before content switch

// Like the hero tree, this always animates: the OS-level reduced-motion
// setting proved too coarse for this marketing surface (see HeroTree.jsx).
const AnimatedTagline = () => {
  const [typed, setTyped] = useState("");
  const [isBusy, setIsBusy] = useState(true); // typing/deleting -> cursor solid
  const [programIndex, setProgramIndex] = useState(0);
  const [programLeaving, setProgramLeaving] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let audienceIndex = 0;
    let cycles = 0;
    const schedule = (fn, ms) => {
      timerRef.current = setTimeout(fn, ms);
    };

    const typeChar = (i) => {
      const word = AUDIENCES[audienceIndex];
      setTyped(word.slice(0, i));
      if (i < word.length) {
        schedule(() => typeChar(i + 1), TYPE_MS);
      } else {
        setIsBusy(false); // cursor resumes blinking while the word holds
        schedule(startDelete, HOLD_MS);
      }
    };

    const startDelete = () => {
      setIsBusy(true);
      deleteChar(AUDIENCES[audienceIndex].length - 1);
    };

    const deleteChar = (i) => {
      const word = AUDIENCES[audienceIndex];
      setTyped(word.slice(0, i));
      if (i > 0) {
        schedule(() => deleteChar(i - 1), DELETE_MS);
      } else {
        schedule(nextCycle, GAP_MS);
      }
    };

    const nextCycle = () => {
      cycles += 1;
      audienceIndex = cycles % AUDIENCES.length;
      if (cycles % 2 === 0) {
        // Swap the program block inside the typing gap: fade the old text
        // down, switch content, rise the new text in, then resume typing.
        setProgramLeaving(true);
        schedule(() => {
          setProgramIndex((p) => (p + 1) % PROGRAMS.length);
          setProgramLeaving(false);
          schedule(() => typeChar(1), 60);
        }, SWAP_LEAVE_MS);
        return;
      }
      typeChar(1);
    };

    typeChar(1);
    return () => clearTimeout(timerRef.current);
  }, []);

  const program = PROGRAMS[programIndex];

  return (
    <>
      <h2 className={styles.tagline}>
        The AI-Native Chronic Care Platform
        <span className={styles.taglineLine2}>
          <strong>Built for {typed}</strong>
          <span
            className={`${styles.cursor} ${isBusy ? styles.cursorSolid : ""}`}
          >
            _
          </span>
        </span>
      </h2>
      <div
        className={`${styles.program} ${
          programLeaving ? styles.programLeaving : styles.programEntering
        }`}
      >
        <p className={styles.programLabel}>
          <span className={styles.programAcronym}>{program.acronym}</span>
          {" — "}
          {program.name}
        </p>
        <p className={styles.programDesc}>{program.description}</p>
      </div>
    </>
  );
};

export default AnimatedTagline;
